import React, {forwardRef, useRef} from 'react'

import {
  Accordion as RadixAccordionPrimitiveRoot,
  AccordionItem as RadixAccordionPrimitiveItem,
  AccordionHeader as RadixAccordionPrimitiveHeader,
  AccordionTrigger as RadixAccordionPrimitiveTrigger,
  AccordionContent as RadixAccordionPrimitiveContent
} from '@radix-ui/react-accordion'

import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks/useRefObjectAsForwardedRef'
import type {
  AccordionSingleProps,
  AccordionMultipleProps,
  AccordionItemProps,
  AccordionHeaderProps,
  AccordionTriggerProps,
  AccordionContentProps
} from './types'

const AccordionSingleElement = forwardRef(({children, ...props}: AccordionSingleProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveRoot ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveRoot>
  )
})
AccordionSingleElement.displayName = 'AccordionSingleElement'

const AccordionMultipleElement = forwardRef(({children, ...props}: AccordionMultipleProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveRoot ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveRoot>
  )
})
AccordionMultipleElement.displayName = 'AccordionMultipleElement'

type AccordionSingleImplProps = {
  type: 'single'
} & AccordionSingleProps

type AccordionMultipleImplProps = {
  type: 'multiple'
} & AccordionMultipleProps
/**
 * Contains all the parts of an accordion.
 */
const Root = forwardRef((props: AccordionSingleImplProps | AccordionMultipleImplProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)

  const {type} = props

  if (type === 'multiple') {
    return <AccordionMultipleElement ref={innerRef} {...props} />
  }
  return <AccordionSingleElement ref={innerRef} {...props} />
})

/**
 * Contains all the parts of a collapsible section.
 */
const Item = forwardRef(({children, ...props}: AccordionItemProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveItem ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveItem>
  )
})

/**
 * Wraps an `Accordion.Trigger`. Use the `asChild` prop to update it to the appropriate heading level for your page.
 */
const Header = forwardRef(({children, ...props}: AccordionHeaderProps, forwardedRef) => {
  const innerRef = useRef<HTMLHeadingElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveHeader ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveHeader>
  )
})

/**
 * Toggles the collapsed state of its associated item. It should be nested inside of an `Accordion.Header`.
 */
const Trigger = forwardRef(({children, ...props}: AccordionTriggerProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveTrigger ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveTrigger>
  )
})

/**
 * Contains the collapsible content for an item.
 */
const Content = forwardRef(({children, ...props}: AccordionContentProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixAccordionPrimitiveContent ref={innerRef} {...props}>
      {children}
    </RadixAccordionPrimitiveContent>
  )
})

// exports
Root.displayName = 'Accordion'
Item.displayName = 'Item'
Header.displayName = 'Header'
Trigger.displayName = 'Trigger'
Content.displayName = 'Content'

export default Object.assign(Root, {Item, Header, Trigger, Content})
